/**
 * Client-Side Logger Utility
 *
 * Provides environment-aware logging that:
 * - Only outputs in development mode
 * - Integrates with Sentry for production error reporting
 * - Uses consistent prefixes for filtering
 *
 * @example
 * ```typescript
 * import { logger } from '~/utils/client-logger';
 *
 * logger.debug('MyComponent', 'Processing data', { count: 10 });
 * logger.info('MyComponent', 'Operation completed');
 * logger.warn('MyComponent', 'Deprecated feature used');
 * logger.error('MyComponent', 'Failed to fetch data', error);
 * ```
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  /** Enable all logging (default: development only) */
  enabled: boolean;
  /** Minimum level to output (debug < info < warn < error) */
  minLevel: LogLevel;
  /** Send errors to Sentry */
  reportToSentry: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Check if we're in development mode
 */
function isDevelopment(): boolean {
  if (typeof window === 'undefined') {
    return process.env.NODE_ENV === 'development';
  }
  // In browser, check for localhost or staging
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname.includes('staging')
  );
}

/**
 * Get Sentry client if available
 */
function getSentry(): { captureException: (err: unknown) => void } | null {
  if (typeof window !== 'undefined' && (window as unknown as { Sentry?: unknown }).Sentry) {
    return (window as unknown as { Sentry: { captureException: (err: unknown) => void } }).Sentry;
  }
  return null;
}

/**
 * Default configuration based on environment
 */
function getDefaultConfig(): LoggerConfig {
  const dev = isDevelopment();
  return {
    enabled: dev,
    minLevel: dev ? 'debug' : 'error',
    reportToSentry: !dev,
  };
}

let config: LoggerConfig = getDefaultConfig();

/**
 * Configure the logger
 */
export function configureLogger(options: Partial<LoggerConfig>): void {
  config = { ...config, ...options };
}

/**
 * Reset logger to default configuration
 */
export function resetLoggerConfig(): void {
  config = getDefaultConfig();
}

/**
 * Format log message with prefix
 */
function formatMessage(component: string, message: string): string {
  return `[${component}] ${message}`;
}

/**
 * Check if a log level should be output
 */
function shouldLog(level: LogLevel): boolean {
  if (!config.enabled && level !== 'error') {
    return false;
  }
  return LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
}

/**
 * Core logging function
 */
function log(
  level: LogLevel,
  component: string,
  message: string,
  ...args: unknown[]
): void {
  if (!shouldLog(level)) {
    // Still report errors to Sentry even if console is disabled
    if (level === 'error' && config.reportToSentry) {
      const sentry = getSentry();
      const error = args.find((arg) => arg instanceof Error);
      if (sentry && error) {
        sentry.captureException(error);
      }
    }
    return;
  }

  const formattedMessage = formatMessage(component, message);

  switch (level) {
    case 'debug':
      // eslint-disable-next-line no-console
      console.log(formattedMessage, ...args);
      break;
    case 'info':
      // eslint-disable-next-line no-console
      console.log(formattedMessage, ...args);
      break;
    case 'warn':
      // eslint-disable-next-line no-console
      console.warn(formattedMessage, ...args);
      break;
    case 'error':
      // eslint-disable-next-line no-console
      console.error(formattedMessage, ...args);

      // Report errors to Sentry
      if (config.reportToSentry) {
        const sentry = getSentry();
        const error = args.find((arg) => arg instanceof Error);
        if (sentry && error) {
          sentry.captureException(error);
        }
      }
      break;
  }
}

/**
 * Client-side logger with environment-aware output
 */
export const logger = {
  /**
   * Debug-level logging (development only)
   */
  debug(component: string, message: string, ...args: unknown[]): void {
    log('debug', component, message, ...args);
  },

  /**
   * Info-level logging (development only by default)
   */
  info(component: string, message: string, ...args: unknown[]): void {
    log('info', component, message, ...args);
  },

  /**
   * Warning-level logging
   */
  warn(component: string, message: string, ...args: unknown[]): void {
    log('warn', component, message, ...args);
  },

  /**
   * Error-level logging (always active, reports to Sentry)
   */
  error(component: string, message: string, ...args: unknown[]): void {
    log('error', component, message, ...args);
  },

  /**
   * Create a scoped logger for a specific component
   */
  scope(component: string) {
    return {
      debug: (message: string, ...args: unknown[]) =>
        log('debug', component, message, ...args),
      info: (message: string, ...args: unknown[]) =>
        log('info', component, message, ...args),
      warn: (message: string, ...args: unknown[]) =>
        log('warn', component, message, ...args),
      error: (message: string, ...args: unknown[]) =>
        log('error', component, message, ...args),
    };
  },
};

export default logger;
